"use client";
import GradientButton from "@/components/shared/html/GradientButton";
import useUserTimezone from "@/hooks/shared/useUserTimezone";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { ImSpinner10 } from "react-icons/im";
import { useDict } from "@/components/providers/LocaleProvider";

interface FormData {
  name: string;
  email: string;
  message: string;
  phone: string;
  placeholder: string;
}

type Field = {
  id: string;
  label: string;
  name: keyof FormData;
  required: boolean;
  textarea?: boolean;
  placeholder?: string;
  type: "text" | "email";
};

const ContactForm = () => {
  const info = useUserTimezone();
  const t = useDict();

  const [status, setStatus] = useState<"idle" | "loading">("idle");
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    message: "",
    phone: "",
    placeholder: "",
  });

  const contactFields: Field[] = [
    {
      id: "name",
      label: t.home.contact.fields.name.label,
      name: "name",
      placeholder: t.home.contact.fields.name.placeholder,
      required: true,
      type: "text",
    },
    {
      id: "email",
      label: t.home.contact.fields.email.label,
      name: "email",
      placeholder: t.home.contact.fields.email.placeholder,
      required: true,
      type: "email",
    },
    {
      id: "phone",
      label: t.home.contact.fields.phone.label,
      name: "phone",
      placeholder: t.home.contact.fields.phone.placeholder,
      required: false,
      type: "text",
    },
    {
      id: "message",
      label: t.home.contact.fields.message.label,
      name: "message",
      required: true,
      placeholder: t.home.contact.fields.message.placeholder,
      textarea: true,
      type: "text",
    },
  ];

  const handleInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (status === "loading") return;

    // Check required fields
    for (const field of contactFields) {
      if (field.required && !formData[field.name].trim()) {
        toast.error(t.home.contact.requiredError.replace("{field}", field.label));
        return;
      }
    }

    setStatus("loading");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, userTimezone: info?.label }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || t.home.contact.successDefault);
        setFormData({
          name: "",
          email: "",
          message: "",
          phone: "",
          placeholder: "",
        });
      } else {
        toast.error(data.error || t.home.contact.errorDefault);
      }
    } catch {
      toast.error(t.home.contact.genericError);
    } finally {
      setStatus("idle");
    }
  };

  return (
    <section className="mx-auto w-full p-4 lg:p-6 space-y-8 bg-[#1c212b] rounded-3xl text-white border border-white/10">
      <p className="section-title text-white">{t.home.contact.letsGetStarted}</p>
      <form onSubmit={handleSubmit} className="space-y-5">
        {contactFields.map((field, index) => (
          <div key={index}>
            <label
              htmlFor={field.id}
              className="block text-sm font-semibold text-white/80 mb-1"
            >
              {field.label}
              {field.required && " *"}
            </label>
            {field.textarea ? (
              <textarea
                id={field.id}
                name={field.name}
                value={formData[field.name]}
                placeholder={field.placeholder}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-3 py-2 rounded-md bg-[#14171f] border border-white/10 text-white/90 placeholder:text-white/60 focus:outline-none focus:ring-2 focus:ring-[#846dff]/60 "
              />
            ) : (
              <input
                id={field.id}
                type={field.type}
                name={field.name}
                value={formData[field.name]}
                placeholder={field.placeholder}
                onChange={handleInputChange}
                className="w-full px-3 py-2 rounded-md bg-[#14171f] border border-white/10 text-white/90 placeholder:text-white/60 focus:outline-none focus:ring-2 focus:ring-[#846dff]/60"
              />
            )}
          </div>
        ))}
        <GradientButton
          disabled={status === "loading"}
          type="submit"
          className="text-white disabled:opacity-50 py-3 px-4 min-h-12 w-full flex justify-center items-center transition"
        >
          {status === "loading" ? (
            <ImSpinner10 className=" animate-spin text-xl" />
          ) : (
            t.home.contact.submit
          )}
        </GradientButton>
      </form>
    </section>
  );
};

export default ContactForm;
